import { supabase } from '../lib/supabase'
import type {
  WppOrigin,
  WppTicketHeader,
  WppTicketMessage,
  WhatsappTicketListParams,
} from '../types/whatsappTickets'
import {
  extractBlipRatingFromText,
  isMissingTicketRating,
  ratingScaleForOrigin,
} from '../utils/blipTicketRating'

const BATCH_SIZE = 1000
const IN_CHUNK = 80

function maxRatingForOrigin(origin: WppOrigin | undefined): number {
  const scale = ratingScaleForOrigin(origin)
  return scale[scale.length - 1] ?? 5
}

function toNumberRating(value: unknown): number | null {
  if (value == null) return null
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : null
}

function normalizeHeader(row: WppTicketHeader): WppTicketHeader {
  return {
    ...row,
    rating: toNumberRating(row.rating),
  }
}

export const whatsappTicketsService = {
  async listByFilters(params: WhatsappTicketListParams): Promise<WppTicketHeader[]> {
    const {
      origin,
      dataInicio,
      dataFim,
      agente,
      status,
      classification,
      rating,
      telefone,
      protocolo,
    } = params

    let query = supabase
      .from('wpp_ticket_list')
      .select('*')
      .eq('origin', origin)
      .gte('start_datetime', new Date(`${dataInicio}T00:00:00`).toISOString())
      .lte('start_datetime', new Date(`${dataFim}T23:59:59`).toISOString())

    if (agente) query = query.eq('agent', agente)
    if (status) query = query.eq('status', status)
    if (classification) query = query.eq('classification', classification)
    if (telefone) query = query.ilike('phone', `%${telefone}%`)
    if (protocolo) query = query.ilike('protocol', `%${protocolo}%`)

    query = query.order('start_datetime', { ascending: false })

    const allData: WppTicketHeader[] = []
    let from = 0
    let hasMore = true

    while (hasMore) {
      const pagedQuery = query.range(from, from + BATCH_SIZE - 1)
      const { data, error } = await pagedQuery
      if (error) throw error

      if (data && data.length > 0) {
        allData.push(...(data as WppTicketHeader[]).map(normalizeHeader))
        if (data.length < BATCH_SIZE) hasMore = false
        else from += BATCH_SIZE
      } else {
        hasMore = false
      }
    }

    const enriched = await this.enrichMissingRatingsFromMessages(allData, origin)

    if (rating) {
      const num = Number(rating)
      if (!Number.isNaN(num)) {
        return enriched.filter((h) => h.rating === num)
      }
    }

    return enriched
  },

  /**
   * Para tickets com rating 0/null, busca mensagens e preenche a nota CSAT da Blip.
   */
  async enrichMissingRatingsFromMessages(
    headers: WppTicketHeader[],
    origin?: WppOrigin,
  ): Promise<WppTicketHeader[]> {
    const missing = headers.filter((h) => isMissingTicketRating(h.rating))
    if (missing.length === 0) return headers

    const maxRating = maxRatingForOrigin(origin ?? headers[0]?.origin)
    const ratingByTicketId = new Map<number, number>()
    const ids = missing.map((h) => h.id)

    for (let i = 0; i < ids.length; i += IN_CHUNK) {
      const chunk = ids.slice(i, i + IN_CHUNK)

      // 1) Tentativa leve: só mensagens que citam nota/avaliação
      let rows: Array<{ ticket_list_id: number; message: string | null; datetime: string }> = []
      const filtered = await supabase
        .from('wpp_ticket_detail')
        .select('ticket_list_id, message, datetime')
        .in('ticket_list_id', chunk)
        .or('message.ilike.%Nota%,message.ilike.%avalia%')

      if (!filtered.error && filtered.data) {
        rows = filtered.data as typeof rows
      }

      rows.sort((a, b) => {
        const ta = a.datetime ? new Date(a.datetime).getTime() : 0
        const tb = b.datetime ? new Date(b.datetime).getTime() : 0
        return tb - ta
      })

      for (const row of rows) {
        const ticketId = Number(row.ticket_list_id)
        if (ratingByTicketId.has(ticketId)) continue
        const extracted = extractBlipRatingFromText(row.message, maxRating)
        if (extracted != null) ratingByTicketId.set(ticketId, extracted)
      }

      // Fallback: mensagens completas dos tickets que ainda ficaram sem nota
      const stillMissing = chunk.filter((id) => !ratingByTicketId.has(id))
      if (stillMissing.length > 0) {
        const full = await supabase
          .from('wpp_ticket_detail')
          .select('ticket_list_id, message, datetime')
          .in('ticket_list_id', stillMissing)
        if (!full.error && full.data) {
          const extra = [...(full.data as typeof rows)].sort((a, b) => {
            const ta = a.datetime ? new Date(a.datetime).getTime() : 0
            const tb = b.datetime ? new Date(b.datetime).getTime() : 0
            return tb - ta
          })
          for (const row of extra) {
            const ticketId = Number(row.ticket_list_id)
            if (ratingByTicketId.has(ticketId)) continue
            const extracted = extractBlipRatingFromText(row.message, maxRating)
            if (extracted != null) ratingByTicketId.set(ticketId, extracted)
          }
        }
      }
    }

    if (ratingByTicketId.size === 0) return headers

    return headers.map((h) => {
      if (!isMissingTicketRating(h.rating)) return h
      const extracted = ratingByTicketId.get(h.id)
      return extracted != null ? { ...h, rating: extracted } : h
    })
  },

  async listByProtocols(protocols: string[], origin: WppOrigin): Promise<WppTicketHeader[]> {
    const headers: WppTicketHeader[] = []
    for (let i = 0; i < protocols.length; i += IN_CHUNK) {
      const chunk = protocols.slice(i, i + IN_CHUNK)
      const { data, error } = await supabase
        .from('wpp_ticket_list')
        .select('*')
        .in('protocol', chunk)
        .eq('origin', origin)
      if (error) throw error
      if (data) headers.push(...(data as WppTicketHeader[]).map(normalizeHeader))
    }
    return this.enrichMissingRatingsFromMessages(headers, origin)
  },

  async getById(id: number, origin: WppOrigin): Promise<WppTicketHeader | null> {
    const { data, error } = await supabase
      .from('wpp_ticket_list')
      .select('*')
      .eq('id', id)
      .eq('origin', origin)
      .maybeSingle()
    if (error) throw error
    if (!data) return null
    const [enriched] = await this.enrichMissingRatingsFromMessages(
      [normalizeHeader(data as WppTicketHeader)],
      origin,
    )
    return enriched
  },

  async getByProtocolOrigin(protocol: string, origin: WppOrigin): Promise<WppTicketHeader | null> {
    const { data, error } = await supabase
      .from('wpp_ticket_list')
      .select('*')
      .eq('protocol', protocol)
      .eq('origin', origin)
      .maybeSingle()
    if (error) throw error
    if (!data) return null
    const [enriched] = await this.enrichMissingRatingsFromMessages(
      [normalizeHeader(data as WppTicketHeader)],
      origin,
    )
    return enriched
  },

  async getMessagesByTicketListId(ticketListId: number): Promise<WppTicketMessage[]> {
    const { data, error } = await supabase
      .from('wpp_ticket_detail')
      .select('*')
      .eq('ticket_list_id', ticketListId)
      .order('datetime', { ascending: true })
    if (error) throw error
    return (data as WppTicketMessage[]) ?? []
  },
}
